"use client";

import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Card,
  Switch,
  Paper,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs, { Dayjs } from "dayjs";
import { useFormikContext } from "formik";
import React, { useState, useEffect } from "react";

import StyledChip from "@/components/atoms/Chip";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import { CampaignFormMessages } from "@/messages/campaigns/campaign-form.messages";
import SchedulingBuilder from "./SchedulingBuilder";
import { HOUR_OPTIONS, WEEK_DAYS, SCHEDULING_CONSTANTS } from "./constants";
import {
  isValidTimeFormat,
  isDateInPast,
  isDateInRange,
  parseHourToDayjs,
  formatDateForDisplay,
  formatDateTimeForAPI,
  sortSchedulesByDate,
  filterNewDates,
  createScheduleFromDate,
} from "./utils";

type Props = {
  messages: CampaignFormMessages;
};

const Calendar = ({ messages }: Props) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<CampaignFormData>();

  const isEditingMode = values.isEditing;
  const isCampaignOnHold = values.isHoldStatus;
  const isCampaignConfirmed = values.isConfirmedStatus;
  const canEditSchedules = isEditingMode && !isCampaignOnHold && !isCampaignConfirmed;

  const [selectedHours, setSelectedHours] = useState<string[]>([]);
  const [customHour, setCustomHour] = useState<string>("");
  const [selectedWeekDays, setSelectedWeekDays] = useState<string[]>([]);
  const [specificDays, setSpecificDays] = useState<Dayjs[]>([]);
  const [programmedDates, setProgrammedDates] = useState<Dayjs[]>([]);
  const [fixedProgramTime, setFixedProgramTime] = useState<string>("");
  const [specificDayTemp, setSpecificDayTemp] = useState<Dayjs | null>(null);
  const [programmedDateTemp, setProgrammedDateTemp] = useState<Dayjs | null>(null);
  const [schedulingError, setSchedulingError] = useState<string>("");

  const handleClearSchedules = () => {
    setSelectedHours([]);
    setSpecificDays([]);
    setProgrammedDates([]);
    setFixedProgramTime("");
    setSchedulingError("");
    setFieldValue("schedules", []);
  };

  const handleImmediateSwitchChange = (checked: boolean) => {
    setFieldValue("isImmediateScheduling", checked);
    if (checked) {
      handleClearSchedules();
    }
  };

  const handleDelete = (scheduleToDelete: { id?: number; date: string }) => {
    const updatedSchedules = values.schedules.filter(
      (schedule) => schedule.date !== scheduleToDelete.date
    );

    if (scheduleToDelete.id) {
      const updatedRemovedSchedules = [...values.removedSchedules, scheduleToDelete.id];
      setFieldValue("removedSchedules", updatedRemovedSchedules);
    }

    setFieldValue("schedules", updatedSchedules);
  };

  const now = dayjs();
  const startDate = values.startDate ? dayjs(values.startDate) : undefined;
  const endDate = values.endDate ? dayjs(values.endDate).endOf("day") : undefined;

  const addHourToSelection = (hourString: string) => {
    if (!isValidTimeFormat(hourString)) return;
    if (!selectedHours.includes(hourString)) {
      setSchedulingError("");
      setSelectedHours((prev) => [...prev, hourString]);
    }
  };

  const toggleHour = (hhmm: string) => {
    setSchedulingError("");
    setSelectedHours((prev) =>
      prev.includes(hhmm) ? prev.filter((h) => h !== hhmm) : [...prev, hhmm]
    );
  };

  const toggleWeekDay = (key: string) => {
    setSchedulingError("");
    setSelectedWeekDays((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const addSpecificDay = () => {
    if (!specificDayTemp) return;

    const selectedDate = dayjs(specificDayTemp);
    if (!selectedDate.isValid()) return;

    const dayStart = selectedDate.startOf("day");

    if (isDateInPast(dayStart)) {
      setSchedulingError(messages.schedulingDateInPastError);
      return;
    }

    if (!isDateInRange(dayStart, startDate, endDate)) {
      setSchedulingError(messages.schedulingDateOutOfRangeError);
      return;
    }

    const dayAlreadyExists = specificDays.some(
      (existingDay) =>
        existingDay && existingDay.isValid() && existingDay.startOf("day").isSame(dayStart)
    );

    if (!dayAlreadyExists) {
      setSchedulingError("");
      setSpecificDays((prev) => [...prev, dayStart]);
      setSpecificDayTemp(null);
    }
  };

  const addProgrammedDate = () => {
    if (!programmedDateTemp || !programmedDateTemp.isValid()) return;

    if (isDateInPast(programmedDateTemp)) {
      setSchedulingError(messages.schedulingDateInPastError);
      return;
    }

    const dateAlreadyExists = programmedDates.some(
      (existingDate) => existingDate.isValid() && existingDate.isSame(programmedDateTemp, "minute")
    );

    if (!dateAlreadyExists) {
      setSchedulingError("");
      setProgrammedDates((prev) => [...prev, programmedDateTemp]);
      setProgrammedDateTemp(null);
    }
  };

  const generateSchedules = () => {
    if (values.disabledField) return;

    setSchedulingError("");

    let generated: { date: string; sent?: boolean }[] = [];

    if (values.isImmediateScheduling) {
      const immediateTime = dayjs().add(
        SCHEDULING_CONSTANTS.MINUTES_TO_ADD_FOR_IMMEDIATE_SENDING,
        "minute"
      );
      generated = [createScheduleFromDate(formatDateTimeForAPI(immediateTime))];
      setFieldValue("schedules", generated);
      return;
    }

    const hours = selectedHours;
    const hasRecurrent =
      hours.length > 0 && (selectedWeekDays.length > 0 || specificDays.length > 0);
    const hasProgrammed = programmedDates.length > 0;

    if (!hasRecurrent && !hasProgrammed) {
      const hasHours = hours.length > 0;
      const hasDays = selectedWeekDays.length > 0 || specificDays.length > 0;
      const hasProgrammedDates = programmedDates.length > 0;

      if (!hasHours && !hasDays && !hasProgrammedDates) {
        setSchedulingError(messages.schedulingNoSchedulingConfiguredError);
      } else if (hasHours && !hasDays && !hasProgrammedDates) {
        setSchedulingError(messages.schedulingNoDaysSelectedError);
      } else if (!hasHours && (hasDays || hasProgrammedDates)) {
        setSchedulingError(messages.schedulingNoHoursSelectedError);
      }
      return;
    }

    if (hasRecurrent) {
      const uniqueDates = new Set<string>();

      if (selectedWeekDays.length > 0 && startDate && endDate) {
        let cursor = startDate.startOf("day");
        const end = endDate.endOf("day");
        const selectedIdx = WEEK_DAYS.filter((d) => selectedWeekDays.includes(d.key)).map(
          (d) => d.index
        );
        while (cursor.isSame(end) || cursor.isBefore(end)) {
          if (selectedIdx.includes(cursor.day())) {
            for (const hour of hours) {
              const dateTimeObj = parseHourToDayjs(cursor, hour);
              if (!isDateInPast(dateTimeObj)) {
                const dateTime = formatDateTimeForAPI(dateTimeObj);
                uniqueDates.add(dateTime);
              }
            }
          }
          cursor = cursor.add(1, "day");
        }
      }

      if (specificDays.length > 0) {
        for (const specificDay of specificDays) {
          if (specificDay && specificDay.isValid()) {
            for (const hour of hours) {
              const dateTimeObj = parseHourToDayjs(specificDay, hour);
              if (!isDateInPast(dateTimeObj)) {
                const dateTime = formatDateTimeForAPI(dateTimeObj);
                uniqueDates.add(dateTime);
              }
            }
          }
        }
      }

      generated = Array.from(uniqueDates)
        .sort()
        .map((date) => createScheduleFromDate(date));
    }

    if (hasProgrammed) {
      const programmedSchedules = programmedDates
        .filter((date) => date && date.isValid() && !isDateInPast(date))
        .map((date) => createScheduleFromDate(formatDateTimeForAPI(date)));

      const mergedSchedules = [...generated, ...programmedSchedules];
      const uniqueDates = Array.from(new Set(mergedSchedules.map((schedule) => schedule.date)));
      generated = uniqueDates.map((date) => createScheduleFromDate(date));
    }

    if (
      generated.length === 0 &&
      (selectedHours.length > 0 ||
        specificDays.length > 0 ||
        selectedWeekDays.length > 0 ||
        programmedDates.length > 0)
    ) {
      setSchedulingError(messages.schedulingAllDatesInPastError);
      return;
    }

    if (generated.length > 0) {
      const hasExistingSchedules = isEditingMode && values.schedules && values.schedules.length > 0;

      if (hasExistingSchedules) {
        const existingSchedules = values.schedules;
        const newDates = generated.map((schedule) => schedule.date);
        const onlyNewDates = filterNewDates(newDates, existingSchedules);

        const combinedSchedules = [
          ...existingSchedules,
          ...onlyNewDates.map((date) => createScheduleFromDate(date)),
        ];

        const sortedSchedules = sortSchedulesByDate(combinedSchedules);
        setFieldValue("schedules", sortedSchedules);
      } else {
        setFieldValue("schedules", generated);
      }
    }
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.campaignSchedulingTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            {!isEditingMode && (
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}
                >
                  <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <MDBox>
                      <MDTypography variant="button">
                        {messages.schedulingImmediateSwitchLabel}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        {messages.schedulingImmediateSwitchDescription}
                      </MDTypography>
                    </MDBox>
                    <Switch
                      checked={values.isImmediateScheduling}
                      onChange={(_, c) => handleImmediateSwitchChange(c)}
                    />
                  </MDBox>
                  <MDBox mt={1.5} p={1.5} sx={{ backgroundColor: "action.hover", borderRadius: 1 }}>
                    <MDTypography variant="caption" color="text" display="block">
                      {messages.schedulingImmediateSwitchHelper}
                    </MDTypography>
                  </MDBox>
                </Paper>
              </Grid>
            )}

            {(canEditSchedules || (!isEditingMode && !values.isImmediateScheduling)) && (
              <>
                {canEditSchedules ? (
                  <Grid size={{ xs: 12 }}>
                    <Accordion
                      disableGutters
                      defaultExpanded={false}
                      sx={{
                        border: (t) => `1px solid ${t.palette.divider}`,
                        borderRadius: 2,
                        boxShadow: "none",
                        overflow: "hidden",
                        "&:before": { display: "none" },
                        "& .MuiAccordionSummary-root": {
                          backgroundColor: "transparent",
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          "&.Mui-expanded": {
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                            backgroundColor: "action.hover",
                          },
                        },
                        "& .MuiAccordionSummary-expandIconWrapper": {
                          color: "text.primary",
                        },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <MDTypography variant="h6" fontWeight="bold">
                          {messages.schedulingAddNewSendsTitle}
                        </MDTypography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                          <SchedulingBuilder
                            messages={messages}
                            HOUR_OPTIONS={HOUR_OPTIONS}
                            selectedHours={selectedHours}
                            setSelectedHours={setSelectedHours}
                            addHourToSelection={addHourToSelection}
                            toggleHour={toggleHour}
                            customHour={customHour}
                            setCustomHour={setCustomHour}
                            WEEK_DAYS={WEEK_DAYS}
                            selectedWeekDays={selectedWeekDays}
                            toggleWeekDay={toggleWeekDay}
                            specificDayTemp={specificDayTemp}
                            setSpecificDayTemp={setSpecificDayTemp}
                            startDate={startDate}
                            endDate={endDate}
                            specificDays={specificDays}
                            setSpecificDays={setSpecificDays}
                            addSpecificDay={addSpecificDay}
                            programmedDateTemp={programmedDateTemp}
                            setProgrammedDateTemp={setProgrammedDateTemp}
                            fixedProgramTime={fixedProgramTime}
                            setFixedProgramTime={setFixedProgramTime}
                            programmedDates={programmedDates}
                            setProgrammedDates={setProgrammedDates}
                            addProgrammedDate={addProgrammedDate}
                            generateSchedules={generateSchedules}
                            schedulingError={schedulingError}
                            isEditMode={isEditingMode}
                          />
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                ) : (
                  <>
                    <SchedulingBuilder
                      messages={messages}
                      HOUR_OPTIONS={HOUR_OPTIONS}
                      selectedHours={selectedHours}
                      setSelectedHours={setSelectedHours}
                      addHourToSelection={addHourToSelection}
                      toggleHour={toggleHour}
                      customHour={customHour}
                      setCustomHour={setCustomHour}
                      WEEK_DAYS={WEEK_DAYS}
                      selectedWeekDays={selectedWeekDays}
                      toggleWeekDay={toggleWeekDay}
                      specificDayTemp={specificDayTemp}
                      setSpecificDayTemp={setSpecificDayTemp}
                      startDate={startDate}
                      endDate={endDate}
                      specificDays={specificDays}
                      setSpecificDays={setSpecificDays}
                      addSpecificDay={addSpecificDay}
                      programmedDateTemp={programmedDateTemp}
                      setProgrammedDateTemp={setProgrammedDateTemp}
                      fixedProgramTime={fixedProgramTime}
                      setFixedProgramTime={setFixedProgramTime}
                      programmedDates={programmedDates}
                      setProgrammedDates={setProgrammedDates}
                      addProgrammedDate={addProgrammedDate}
                      generateSchedules={generateSchedules}
                      handleClearSchedules={handleClearSchedules}
                      schedulingError={schedulingError}
                      isEditMode={isEditingMode}
                    />
                  </>
                )}
              </>
            )}

            {values.schedules.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}
                >
                  <MDTypography variant="h6" gutterBottom>
                    {messages.schedulingGeneratedDatesTitle}
                  </MDTypography>
                  <MDBox display="flex" flexWrap="wrap" gap={1}>
                    {sortSchedulesByDate(values.schedules).map((schedule, index) => (
                      <StyledChip
                        key={index}
                        bgColor="dark"
                        label={formatDateForDisplay(schedule.date)}
                        onDelete={() => handleDelete(schedule)}
                        disabled={values.disabledField || schedule.sent}
                        deleteIcon={
                          <HighlightOffIcon fontSize="medium" style={{ color: "white" }} />
                        }
                      />
                    ))}
                  </MDBox>
                  {touched.schedules && errors.schedules && (
                    <MDTypography
                      variant="caption"
                      color="error"
                      fontWeight="bold"
                      display="block"
                      mt={1}
                    >
                      {errors.schedules as string}
                    </MDTypography>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
};

export default Calendar;
